import { useState } from 'react'
import {
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  MessageSquare,
} from 'lucide-react'
import { toast } from 'sonner'
import { useEmployeeContext } from './EmployeeDetailLayout'
import { noteService } from '../../services/noteService'
import { EmployeeNote } from '../../types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { formatDate } from '../../utils/dateUtils'

export function EmployeeNotesTab() {
  const { employee, notes, isAdmin, fetchNotes } = useEmployeeContext()

  const [showNoteForm, setShowNoteForm] = useState(false)
  const [editingNote, setEditingNote] = useState<EmployeeNote | null>(null)
  const [noteForm, setNoteForm] = useState({ title: '', content: '', isImportant: false })
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  if (!employee) return null

  const handleSaveNote = async () => {
    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      toast.error('El título y contenido son obligatorios')
      return
    }

    setIsSaving(true)
    try {
      if (editingNote) {
        await noteService.update(editingNote.id, noteForm)
        toast.success('Nota actualizada')
      } else {
        await noteService.create({
          employeeId: employee.id,
          ...noteForm,
        })
        toast.success('Nota creada')
      }
      setShowNoteForm(false)
      setEditingNote(null)
      setNoteForm({ title: '', content: '', isImportant: false })
      fetchNotes()
    } catch (error) {
      toast.error('Error al guardar nota')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteNote = async () => {
    if (!deletingNoteId) return
    try {
      await noteService.delete(deletingNoteId)
      toast.success('Nota eliminada')
      setDeletingNoteId(null)
      fetchNotes()
    } catch (error) {
      toast.error('Error al eliminar nota')
    }
  }

  const formatTimeLocal = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Separar notas importantes
  const importantNotes = notes.filter(n => n.isImportant)
  const regularNotes = notes.filter(n => !n.isImportant)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Notas del Empleado
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {notes.length} nota{notes.length !== 1 ? 's' : ''} en total
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => {
              setEditingNote(null)
              setNoteForm({ title: '', content: '', isImportant: false })
              setShowNoteForm(true)
            }}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Nueva Nota
          </button>
        )}
      </div>

      {/* Note Form */}
      {showNoteForm && (
        <div className="card border-2 border-primary-200 dark:border-primary-800">
          <h3 className="text-md font-semibold mb-4 text-gray-900 dark:text-white">
            {editingNote ? 'Editar Nota' : 'Nueva Nota'}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Título
              </label>
              <input
                type="text"
                className="input"
                placeholder="Título de la nota"
                value={noteForm.title}
                onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contenido
              </label>
              <textarea
                className="input min-h-[120px]"
                placeholder="Escribe el contenido de la nota..."
                value={noteForm.content}
                onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                checked={noteForm.isImportant}
                onChange={(e) => setNoteForm({ ...noteForm, isImportant: e.target.checked })}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Marcar como importante
              </span>
            </label>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSaveNote}
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? 'Guardando...' : editingNote ? 'Actualizar' : 'Guardar'}
              </button>
              <button
                onClick={() => {
                  setShowNoteForm(false)
                  setEditingNote(null)
                  setNoteForm({ title: '', content: '', isImportant: false })
                }}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="card text-center py-12">
          <MessageSquare className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            No hay notas para este empleado
          </p>
          {isAdmin && (
            <button
              onClick={() => {
                setEditingNote(null)
                setNoteForm({ title: '', content: '', isImportant: false })
                setShowNoteForm(true)
              }}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Crear Primera Nota
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Notas Importantes */}
          {importantNotes.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                Notas Importantes
                <span className="badge bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs">
                  {importantNotes.length}
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {importantNotes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    isAdmin={isAdmin}
                    formatDate={formatDate}
                    formatTimeLocal={formatTimeLocal}
                    onEdit={(note) => {
                      setEditingNote(note)
                      setNoteForm({
                        title: note.title,
                        content: note.content,
                        isImportant: note.isImportant,
                      })
                      setShowNoteForm(true)
                    }}
                    onDelete={(id) => setDeletingNoteId(id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Notas Regulares */}
          {regularNotes.length > 0 && (
            <div>
              {importantNotes.length > 0 && (
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-gray-400" />
                  Otras Notas
                  <span className="badge badge-gray text-xs">{regularNotes.length}</span>
                </h3>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {regularNotes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    isAdmin={isAdmin}
                    formatDate={formatDate}
                    formatTimeLocal={formatTimeLocal}
                    onEdit={(note) => {
                      setEditingNote(note)
                      setNoteForm({
                        title: note.title,
                        content: note.content,
                        isImportant: note.isImportant,
                      })
                      setShowNoteForm(true)
                    }}
                    onDelete={(id) => setDeletingNoteId(id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Note Confirm */}
      <ConfirmDialog
        isOpen={!!deletingNoteId}
        onClose={() => setDeletingNoteId(null)}
        onConfirm={handleDeleteNote}
        title="Eliminar Nota"
        message="¿Estás seguro de eliminar esta nota?"
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  )
}

interface NoteCardProps {
  note: EmployeeNote
  isAdmin: boolean
  formatDate: (date: string) => string
  formatTimeLocal: (date: string) => string
  onEdit: (note: EmployeeNote) => void
  onDelete: (id: string) => void
}

function NoteCard({ note, isAdmin, formatDate, formatTimeLocal, onEdit, onDelete }: NoteCardProps) {
  return (
    <div
      className={`card transition-all hover:shadow-md ${
        note.isImportant
          ? 'border-l-4 border-l-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'
          : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {note.isImportant && (
              <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
            )}
            <h4 className="font-semibold text-gray-900 dark:text-white truncate">
              {note.title}
            </h4>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-wrap">
            {note.content}
          </p>
          <div className="flex items-center gap-2 mt-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium">{note.createdBy.name}</span>
            <span>•</span>
            <span>{formatDate(note.createdAt)}</span>
            <span>a las</span>
            <span>{formatTimeLocal(note.createdAt)}</span>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => onEdit(note)}
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded text-gray-500 dark:text-gray-400 transition-colors"
              title="Editar nota"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(note.id)}
              className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 dark:text-red-400 transition-colors"
              title="Eliminar nota"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
